/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * This is an a11y-friendly tabbed panel control that deviates from guidance
 * that we have followed in a previous implementation of TabbedPanel as
 * described in the following links:
 * - https://alastairc.ac/2016/05/aria-tabs-ui-problems-and-standards/
 * - http://a11yideas.com/testcode/tabs/tabs_with_ARIA.html
 * - https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html
 * - https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-1/tabs.html
 * These examples use "arrow-key" navigation and automatically change the
 * tab panel content when a new tab header is focused. This type of navigation
 * complicates code and even presents some accessibility issues. For example,
 * screenreader users would struggle to read the content for any tab that is
 * not the last tab because navigating forward from a selected tab would
 * select another tab, overwriting the content.
 *
 * In this TabbedPanel, we only change the tab content if the user clicks
 * the tab or uses an appropriate selection keypress. We do not change the
 * tab content if the header that is focused changes. On tab selection, we
 * jump the focus the tab content. See the following example we took
 * inspiration from:
 * - https://codepen.io/jeffsmith/pen/mPByya/
 *
 * Event handling tab headers is managed through the onTabChanged prop and is
 * executed by the Clickable tab header in this component.
 *
 * The implementation uses function-as-children semantics to allow consumers
 * to place tabs and tab content where they require within their own UX.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {Errors, KAError} from "@repo/logging";

export type TabbedPanelProps<T extends string> = {
    // Will the tabs be on the left or right of the content, or above or below?
    // "horizontal" tabs can be placed to the side of content and "vertical" tabs
    // can sit above or below content.
    orientation: "horizontal" | "vertical";
    // The identifier to use for this panel.
    // This is also used as part of the identifiers for the tabs and their panel
    // contents.
    id: string;
    // The Aria label for the tab list to help differentiate it from other
    // tab lists on the page.
    tabListAriaLabel: string;
    // The key of the tab to be selected. Default is the first tab.
    // The current tab will update to match this value if this prop is changed
    // by the consumer of this control.
    selectedTabKey?: T;
    // Callback for when the selected tab is changed. If a callback is provided
    // then the component is considered to be a controlled component and the
    // parent MUST update selectedTabKey with the new value in order for the
    // tab to change.
    onTabChanged?: (newTabKey: T) => void;
    // Render prop that is called with two functions; one for rendering the
    // tabs and one for rendering the tab panel that shows content. This allows
    // consumers to control where these items get placed.
    children: (
        renderTabs: (
            additionalTabListStyle?: any,
            additionalTabContainerStyle?: any,
            ...additionalTabStyles: Array<any>
        ) => React.ReactNode,
        renderContentPanel: (
            ...additionalContentPanelStyles: Array<any>
        ) => React.ReactNode,
    ) => React.ReactNode;
    // Tabs with render props for the tab and its corresponding content panel.
    tabs: ReadonlyArray<Tab<T>>;
};

export type TabHeader = {
    // Tab header to render as specified by TabbedPanel callers when they
    // initialize the renderTab() function for a Tab.
    tabHeader: React.ReactElement<any>;
    // Determines whether the tab header is interactable.
    isTabDisabled: boolean;
};

export type Tab<T> = {
    // The unique key for this tab.
    key: T;
    // The property that renders the tab itself.
    // The selected state of the tab is provided for the application of visual
    // treatments to the content.
    // The props provided contain a variety of a11y-specific attributes
    // needed for compliance and it is expected that they will be applied
    // to a link component within the returned content. However, as long as the
    // content is a11y-friendly (i.e. can be focused, handles click with given,
    // handler, and follows other rules of a tab), more complex content can be
    // returned.
    renderTab: (isTabSelected: boolean, props?: any) => TabHeader;
    // The property that renders the content. This is only called for the
    // currently selected tab.
    renderContent: () => React.ReactNode;
};

type DefaultProps = {
    orientation: TabbedPanelProps<string>["orientation"];
};

type TabIdentifier = {
    tabKey: string;
    index: number;
};

type TabbedPanelState = {
    selectedTabIdentifier: TabIdentifier;
};

function getTabIndexForKeyOrDefaultIndex<T extends string>(
    tabs: ReadonlyArray<Tab<T>>,
    key?: string,
): number {
    if (!tabs.length) {
        throw new KAError(
            "TabbedPanel expects at least one tab.",
            Errors.Internal,
        );
    }

    // Return either the index with the given key or the first index if no
    // tab with a corresponding key exists or no key is provided.
    const index = tabs.findIndex((tab) => tab.key === key);
    return index >= 0 ? index : 0;
}

function getTabForKeyOrDefaultTab<T extends string>(
    tabs: ReadonlyArray<Tab<T>>,
    key?: string,
): Tab<T> {
    return tabs[getTabIndexForKeyOrDefaultIndex(tabs, key)];
}

class TabbedPanel<T extends string> extends React.Component<
    TabbedPanelProps<T>,
    TabbedPanelState
> {
    // map of tabContentId: Ref which is used to manage focus behavior,
    // allowing this component to find the ref for tab content with the
    // tabContentId
    tabContentPanelRefs: Record<string, React.ElementRef<any>>;

    static defaultProps: DefaultProps = {
        orientation: "vertical",
    };

    constructor(props: TabbedPanelProps<T>) {
        super(props);

        this.state = this.getSelectedTabStateFromProps();
        this.tabContentPanelRefs = {};
    }

    componentDidUpdate(
        prevProps: Readonly<TabbedPanelProps<T>>,
        prevState: Readonly<TabbedPanelState>,
    ): void {
        // TODO(michaelpolyak): Possibly consider breaking into two components. One
        // that has a selected tab prop and reports a change to a parent, and then
        // a parent one that actually does the state tracking.

        // If onTabChanged is provided then the component is considered to be
        // controlled, i.e. the selectedTabKey state is kept in sync with the
        // selectedTabKey prop. Having a clear separation between controlled
        // and uncontrolled modes prevents double rendering (where the state
        // is updated and then the prop is updated after).
        if (this.isControlledComponent()) {
            const {key: prevSelectedKey} = getTabForKeyOrDefaultTab(
                prevProps.tabs,
                prevProps.selectedTabKey,
            );
            const {key: selectedKey} = getTabForKeyOrDefaultTab(
                this.props.tabs,
                this.props.selectedTabKey,
            );

            if (prevSelectedKey !== selectedKey) {
                /**
                 * As per the React documentation, it is perfectly fine to call
                 * setState from componentDidUpdate as long as it is guarded
                 * with a condition that does not rely on the state being set.
                 *
                 * https://reactjs.org/docs/react-component.html#static-getderivedstatefromprops
                 * > If you need to perform a side effect (for example, data
                 * > fetching or an animation) in response to a change in props,
                 * > use componentDidUpdate lifecycle instead.
                 *
                 * https://reactjs.org/docs/react-component.html#componentdidupdate
                 * > You may call setState() immediately in componentDidUpdate()
                 * > but note that it must be wrapped in a condition, or you’ll
                 * > cause an infinite loop.
                 */

                this.setState(this.getSelectedTabStateFromProps());
            }
        }

        // If the user changes tabs, focus on the new tab's content. If the
        // TabbedPanel is managed as a controlled component, this will be
        // called on the second componentDidUpdate() triggered by the
        // state change above
        if (
            prevState.selectedTabIdentifier.index !==
            this.state.selectedTabIdentifier.index
        ) {
            // Focus the tab content
            const {tabContentId} = this.getTabIdentifiers(
                this.state.selectedTabIdentifier.index,
            );

            const tabContentRef = this.tabContentPanelRefs[tabContentId];
            if (!tabContentRef) {
                throw new KAError(
                    `Tab content not found: ${tabContentId}`,
                    Errors.Internal,
                );
            }

            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            tabContentRef.current.focus();
        }
    }

    getSelectedTabStateFromProps(): TabbedPanelState {
        // Called via the TabbedPanel contructor and componentDidUpdate().
        // This function should not be used in any other contexts.
        //
        // Using this function to update the state elsewhere could result in
        // overwriting any pending updates to the selectedTabKey state if the
        // component is uncontrolled. The state returned by this function should
        // only be used if 1) props were just updated, and 2) the component is
        // controlled; or we're in the constructor.
        const {key: selectedKey} = getTabForKeyOrDefaultTab(
            this.props.tabs,
            this.props.selectedTabKey,
        );
        const selectedTabIndex = getTabIndexForKeyOrDefaultIndex(
            this.props.tabs,
            this.props.selectedTabKey,
        );
        return {
            selectedTabIdentifier: {
                tabKey: selectedKey,
                index: selectedTabIndex,
            },
        };
    }

    isControlledComponent(): boolean {
        // The TabbedPanel component is controlled if onTabChanged is present.
        return !!this.props.onTabChanged;
    }

    onTabClick(index: number): void {
        const newKey = this.props.tabs[index].key;
        if (index !== this.state.selectedTabIdentifier.index) {
            if (this.isControlledComponent()) {
                if (!this.props.onTabChanged) {
                    throw new KAError(
                        "TabbedPanel is controlled but" +
                            " this.props.onTabChanged is not present." +
                            " Something is wrong.",
                        Errors.Internal,
                    );
                }
                // If the component is controlled, tell the parent component
                // about the change, and the change will be propogated back via
                // the selectedTabKey prop.
                this.props.onTabChanged(newKey);
            } else {
                // set the selectedTabKey state directly.
                this.setState({
                    selectedTabIdentifier: {
                        tabKey: newKey,
                        index: index,
                    },
                });
            }
        }
    }

    getTabIdentifiers(index: number): {
        tabContentId: string;
        tabId: string;
    } {
        const {id} = this.props;

        return {
            tabId: `ka-${id}-tabbedpanel-tab-${index}`,
            tabContentId: `ka-${id}-tabbedpanel-content`,
        };
    }

    renderTab(
        tab: Tab<T>,
        index: number,
        ...additionalTabStyles: Array<any>
    ): React.ReactNode {
        const {tabId, tabContentId} = this.getTabIdentifiers(index);
        const isTabSelected =
            tab.key === this.state.selectedTabIdentifier.tabKey;

        // Clickable needs a ("true"|"false") literal for the props below
        const isTabSelectedAsLiteral = isTabSelected ? "true" : "false";

        const {tabHeader, isTabDisabled} = tab.renderTab(isTabSelected, {
            id: tabId + "-inner",
        });

        return (
            <Clickable
                aria-controls={tabContentId}
                aria-expanded={isTabSelectedAsLiteral}
                aria-selected={isTabSelectedAsLiteral}
                className={css(...additionalTabStyles)}
                disabled={isTabDisabled}
                id={tabId}
                onClick={() => this.onTabClick(index)}
                role="tab"
                testId={tab.key}
            >
                {() => tabHeader}
            </Clickable>
        );
    }

    renderTabs(
        additionalTabListStyle: any,
        additionalTabContainerStyle: any,
        ...additionalTabStyles: Array<any>
    ): React.ReactElement<React.ComponentProps<"div">> {
        // additionalTabListStyles is a single style object that will be applied
        // to the TabList itself, encapsulated in the div[role="tablist"].
        // additionalTabContainerStyle will be applied to the item that wraps
        // the tab header.
        // All remaining styles will be applied directly to the tab item
        // from additionalTabStyles
        const {tabs, tabListAriaLabel} = this.props;

        return (
            <div key="tablist" className={css(styles.tabsNav)}>
                <div
                    role="tablist"
                    aria-label={tabListAriaLabel}
                    className={css(styles.tablist, additionalTabListStyle)}
                >
                    {tabs.map((tab, index) => {
                        return (
                            <span
                                key={tab.key}
                                className={css(
                                    styles.tab,
                                    additionalTabContainerStyle,
                                )}
                            >
                                {this.renderTab(
                                    tab,
                                    index,
                                    additionalTabStyles,
                                )}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    }

    renderTabContentPanel(
        ...additionalContentPanelStyles: Array<any>
    ): React.ReactElement<any> {
        const {tabs} = this.props;

        const {tabContentId} = this.getTabIdentifiers(
            this.state.selectedTabIdentifier.index,
        );

        this.tabContentPanelRefs[tabContentId] = React.createRef();

        return (
            <div
                id={tabContentId}
                key={tabContentId}
                // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
                ref={this.tabContentPanelRefs[tabContentId]}
                className={css(
                    styles.tabContent,
                    ...additionalContentPanelStyles,
                )}
                // We jump a user's focus to the tab content when they select
                // a new tab. So, the tab content needs to be focusable.
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
                tabIndex="0"
                role="tabpanel"
            >
                {tabs[this.state.selectedTabIdentifier.index].renderContent()}
            </div>
        );
    }

    render(): React.ReactNode {
        const {children, orientation} = this.props;
        return (
            <div
                style={{height: "100%"}}
                className={css(
                    orientation === "horizontal" && styles.horizontal,
                )}
            >
                {children(
                    (
                        additionalTabListStyle,
                        additionalTabContainerStyle,
                        additionalTabStyles,
                    ) =>
                        this.renderTabs(
                            additionalTabListStyle,
                            additionalTabContainerStyle,
                            additionalTabStyles,
                        ),
                    (additionalContentPanelStyles) =>
                        this.renderTabContentPanel(
                            additionalContentPanelStyles,
                        ),
                )}
            </div>
        );
    }
}

const styles = StyleSheet.create({
    tabsNav: {
        width: "auto",
    },
    tablist: {
        flex: "0 0 auto",
        display: "flex",
    },
    tab: {
        flex: "0 0 auto",
        display: "flex",
    },
    tabContent: {
        flex: "1 1 auto",
        overflow: "auto",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    collapsedTabContent: {
        display: "none",
    },
    horizontal: {
        display: "flex",
        flex: "1 1 0",
        overflow: "hidden",
    },
});

export default TabbedPanel;
